import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { auth, db } from '../../firebase.config';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { Link, useNavigate } from 'react-router-dom';
import { FaFileDownload, FaChartLine, FaSignOutAlt, FaNewspaper, FaSearch } from 'react-icons/fa';
import Loader from '../../components/Loader/Loader';
import { formatBlogDate } from '../../utils/formatBlogDate';
import { insightArticleHrefInContext } from '../../utils/insightArticle';
import './InvestorDashboard.css';

interface Report {
  id: string;
  title: string;
  type: 'monthly' | 'quarterly' | 'annual';
  date: any;
  fileURL: string;
  published: boolean;
}

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  date: any;
  published: boolean;
  imageURL?: string;
  slug?: string;
}

const InvestorDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'reports' | 'blog'>('overview');
  const [reports, setReports] = useState<Report[]>([]);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [reportFilter, setReportFilter] = useState<'all' | 'monthly' | 'quarterly' | 'annual'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      if (!currentUser) {
        navigate('/investor-login');
      } else {
        setUser(currentUser);
        loadData();
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const reportsQuery = query(
        collection(db, 'reports'),
        where('published', '==', true),
        orderBy('date', 'desc')
      );
      const reportsSnapshot = await getDocs(reportsQuery);
      setReports(reportsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Report)));

      const blogQuery = query(
        collection(db, 'blogPosts'),
        where('published', '==', true),
        orderBy('date', 'desc')
      );
      const blogSnapshot = await getDocs(blogQuery);
      setBlogPosts(blogSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as BlogPost)));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/investor-login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getReportsByType = (type: string) => {
    return reports.filter(r => r.type === type);
  };

  const filteredReports = reports.filter(r => {
    const matchType = reportFilter === 'all' || r.type === reportFilter;
    const matchSearch = !searchQuery || r.title?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchType && matchSearch;
  });

  const filteredBlogPosts = blogPosts.filter(p =>
    !searchQuery || p.title?.toLowerCase().includes(searchQuery.toLowerCase()) || p.excerpt?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <Loader fullScreen size="large" />;
  }

  return (
    <div className="investor-dashboard">
      <div className="dashboard-header">
        <div className="container">
          <div className="header-content">
            <div>
              <h1>Investor Portal</h1>
              <p className="welcome-text">Welcome, {user?.displayName || user?.email}</p>
            </div>
            <button onClick={handleLogout} className="logout-btn">
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="container">
          <div className="dashboard-tabs">
            <button
              className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <FaChartLine /> Overview
            </button>
            <button
              className={`tab-btn ${activeTab === 'reports' ? 'active' : ''}`}
              onClick={() => setActiveTab('reports')}
            >
              <FaFileDownload /> Reports & Documents ({reports.length})
            </button>
            <button
              className={`tab-btn ${activeTab === 'blog' ? 'active' : ''}`}
              onClick={() => setActiveTab('blog')}
            >
              <FaNewspaper /> Insights & Updates ({blogPosts.length})
            </button>
          </div>

          <div className="dashboard-panel">
            {activeTab === 'overview' && (
              <motion.div
                className="overview-panel"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h2>Portfolio Overview</h2>

                <div className="stats-grid">
                  <div className="stat-card">
                    <h3>Total Reports</h3>
                    <p className="stat-value">{reports.length}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Monthly Factsheets</h3>
                    <p className="stat-value">{getReportsByType('monthly').length}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Quarterly Reports</h3>
                    <p className="stat-value">{getReportsByType('quarterly').length}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Annual Reports</h3>
                    <p className="stat-value">{getReportsByType('annual').length}</p>
                  </div>
                </div>

                <div className="info-section">
                  <h3>Important Information</h3>
                  <p>
                    Access your investment reports, monthly factsheets, and quarterly updates from the 
                    Reports & Documents section. Stay informed with our latest insights and market commentary 
                    in the Insights & Updates section.
                  </p>
                  <p>
                    For any queries or assistance, please contact us at{' '}
                    <a href="mailto:compliance@kyrocapital.in">compliance@kyrocapital.in</a> or <a href="tel:+917611111102">76 111 111 02</a>
                  </p>
                </div>
              </motion.div>
            )}

            {activeTab === 'reports' && (
              <motion.div
                className="reports-panel"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h2>Reports & Communications</h2>
                <div className="dashboard-filters">
                  <div className="search-box">
                    <FaSearch />
                    <input
                      type="text"
                      placeholder="Search reports..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <select value={reportFilter} onChange={(e) => setReportFilter(e.target.value as typeof reportFilter)}>
                    <option value="all">All Types</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>
                {['monthly', 'quarterly', 'annual'].map((type) => {
                  const typeReports = reportFilter === 'all' || reportFilter === type
                    ? getReportsByType(type).filter(r => !searchQuery || r.title?.toLowerCase().includes(searchQuery.toLowerCase()))
                    : [];
                  if (typeReports.length === 0) return null;
                  
                  return (
                    <div key={type} className="report-category">
                      <h3>{type.charAt(0).toUpperCase() + type.slice(1)} Reports</h3>
                      <div className="reports-list">
                        {typeReports.map((report) => (
                          <div key={report.id} className="report-item">
                            <div className="report-info">
                              <h4>{report.title}</h4>
                              <p className="report-date">
                                {report.date?.toDate?.()?.toLocaleDateString?.() ?? '—'}
                              </p>
                            </div>
                            <a
                              href={report.fileURL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="download-btn"
                            >
                              <FaFileDownload /> Download
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {filteredReports.length === 0 && (
                  <div className="empty-state">
                    <p>{reports.length === 0 ? 'No reports available at this time. Check back soon for updates.' : 'No reports match your filters.'}</p>
                  </div>
                )}
              </motion.div>
            )}

            {activeTab === 'blog' && (
              <motion.div
                className="blog-panel"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h2>Insights & Updates</h2>
                <div className="search-box" style={{ marginBottom: '1.5rem', maxWidth: '320px' }}>
                  <FaSearch />
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="blog-list">
                  {filteredBlogPosts.map((post) => (
                    <div key={post.id} className="blog-item">
                      {post.imageURL && (
                        <img src={post.imageURL} alt="" className="blog-item-image" style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8, marginBottom: '0.75rem' }} />
                      )}
                      <h3>{post.title}</h3>
                      <p className="blog-date">{formatBlogDate(post.date)}</p>
                      <p className="blog-excerpt">{post.excerpt}</p>
                      <Link to={insightArticleHrefInContext(post, blogPosts)} className="read-more-btn">
                        Read article
                      </Link>
                    </div>
                  ))}
                </div>

                {filteredBlogPosts.length === 0 && (
                  <div className="empty-state">
                    <p>{blogPosts.length === 0 ? 'No articles available at this time. Check back soon for insights and updates.' : 'No articles match your search.'}</p>
                  </div>
                )}

              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestorDashboard;
